import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import axios from 'axios';
import { useSite } from '@/contexts/SiteContext';
import {
  ArrowRight,
  UserPlus,
  Shield,
  Wallet,
  Eye,
  Globe,
  BarChart3,
  Lock,
  ChevronRight,
  TrendingUp,
  Building2,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logoUrl, companyName } = useSite();
  const [stats, setStats] = useState(null);
  const [funds, setFunds] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, fundsRes] = await Promise.all([
          axios.get(`${API_URL}/api/stats`),
          axios.get(`${API_URL}/api/funds`)
        ]);
        setStats(statsRes.data);
        setFunds(fundsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const getFundName = (fund) => {
    if (i18n.language === 'en' && fund.name_en) return fund.name_en;
    if (i18n.language === 'pt' && fund.name_pt) return fund.name_pt;
    return fund.name;
  };

  const getFundDescription = (fund) => {
    if (i18n.language === 'en' && fund.description_en) return fund.description_en;
    if (i18n.language === 'pt' && fund.description_pt) return fund.description_pt;
    return fund.description;
  };

  const getRiskLabel = (level) => {
    const labels = {
      low: t('funds.low'),
      medium: t('funds.medium'),
      high: t('funds.high')
    };
    return labels[level] || level;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0
    }).format(value);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/b5ef289c-2dca-4d1b-892f-b665eed8360f/images/72b8040d75420cbfcc42aa078ea21559e8a5db35592d878dd20afb1adae93879.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight" data-testid="hero-title">
              {t('hero.title')}
            </h1>
            <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-white text-black hover:bg-zinc-200 rounded-sm px-8"
                onClick={() => navigate('/register')}
                data-testid="hero-cta-register"
              >
                {t('hero.cta1')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                onClick={() => navigate('/funds')}
                data-testid="hero-cta-funds"
              >
                {t('hero.cta2')}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      {stats && (
        <section className="py-16 bg-[#141414] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="data-label mb-2">{t('stats.totalFunds')}</p>
                <p className="data-value text-4xl text-white">{stats.total_funds}</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-center"
              >
                <p className="data-label mb-2">{t('stats.totalRaised')}</p>
                <p className="data-value text-4xl text-white">¥{(stats.total_raised / 100000000).toFixed(1)}億</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <p className="data-label mb-2">{t('stats.investors')}</p>
                <p className="data-value text-4xl text-white">{stats.total_investors.toLocaleString()}</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <p className="data-label mb-2">{t('stats.avgYield')}</p>
                <p className="data-value text-4xl text-emerald-500">{stats.average_yield}%</p>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* 3 Steps Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-3xl sm:text-4xl text-white text-center mb-16"
          >
            {t('steps.title')}
          </motion.h2>
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { icon: UserPlus, step: 'step1', num: '01' },
              { icon: Shield, step: 'step2', num: '02' },
              { icon: Wallet, step: 'step3', num: '03' }
            ].map((item, index) => (
              <motion.div key={item.step} variants={itemVariants}>
                <Card className="card-presage rounded-sm h-full">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <item.icon className="h-10 w-10 text-white" />
                      <span className="data-value text-5xl text-zinc-800">{item.num}</span>
                    </div>
                    <h3 className="font-heading text-xl text-white mb-3">
                      {t(`steps.${item.step}.title`)}
                    </h3>
                    <p className="text-zinc-500">
                      {t(`steps.${item.step}.desc`)}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features/USP Section */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl text-white mb-8">
                {t('features.title')}
              </h2>
              <div className="space-y-6">
                {[
                  { icon: Eye, key: 'transparency' },
                  { icon: Globe, key: 'global' },
                  { icon: BarChart3, key: 'dashboard' },
                  { icon: Lock, key: 'security' }
                ].map((feature) => (
                  <div key={feature.key} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">
                        {t(`features.${feature.key}.title`)}
                      </h3>
                      <p className="text-zinc-500 text-sm">
                        {t(`features.${feature.key}.desc`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/b5ef289c-2dca-4d1b-892f-b665eed8360f/images/b04ac53ed23b2708c1d9403a2d1aff0612ba5067d8027e1f31fd366a7f1836e7.png"
                alt="Architecture"
                className="rounded-sm w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Funds Preview Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-white">
              {t('funds.title')}
            </h2>
            <Link to="/funds">
              <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="view-all-funds">
                {t('funds.viewAll')}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6"
          >
            {funds.map((fund) => (
              <motion.div key={fund.id} variants={itemVariants}>
                <Link to={`/funds/${fund.id}`}>
                  <Card className="card-presage rounded-sm overflow-hidden group cursor-pointer h-full" data-testid={`fund-card-${fund.id}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={fund.image_url || 'https://images.pexels.com/photos/2275008/pexels-photo-2275008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                        alt={getFundName(fund)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {fund.is_guaranteed && (
                        <div className="absolute top-3 right-3 bg-[#D4AF37] text-black text-xs px-2 py-1 rounded-sm font-semibold flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('funds.guaranteed')}{fund.guarantee_rate ? ` ${fund.guarantee_rate}%` : ''}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-white group-hover:text-zinc-300 transition-colors">
                          {getFundName(fund)}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs rounded-sm risk-${fund.risk_level}`}>
                          {getRiskLabel(fund.risk_level)}
                        </span>
                      </div>
                      <p className="text-zinc-500 text-sm mb-4 line-clamp-2">
                        {getFundDescription(fund)}
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="data-label">{t('funds.targetYield')}</p>
                          <p className="data-value text-lg text-emerald-500">{fund.target_yield}%</p>
                        </div>
                        <div>
                          <p className="data-label">{t('funds.minInvestment')}</p>
                          <p className="data-value text-lg text-white">{formatCurrency(fund.min_investment)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Distribution Transparency Section */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
                {t('distribution.title')}
              </h2>
              <p className="text-zinc-500 mb-8">
                {t('distribution.subtitle')}
              </p>
              <Card className="bg-[#0A0A0A] border-white/10 rounded-sm">
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-zinc-400">{t('distribution.grossRevenue')}</span>
                    <span className="data-value text-white">¥1,200,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-zinc-400">{t('distribution.costs')}</span>
                    <span className="data-value text-red-400">- ¥200,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-zinc-400">{t('distribution.netProfit')}</span>
                    <span className="data-value text-white">¥1,000,000</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-zinc-400">{t('distribution.yourShare')}</span>
                    <span className="data-value text-white">10%</span>
                  </div>
                  <div className="flex justify-between items-center py-3 bg-emerald-500/10 -mx-6 px-6 rounded-b-sm">
                    <span className="text-emerald-400 font-semibold">{t('distribution.yourDistribution')}</span>
                    <span className="data-value text-2xl text-emerald-400">¥100,000</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex justify-center"
            >
              <img
                src="https://images.unsplash.com/photo-1766218337264-f41883f217e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjBmaW5hbmNlJTIwaW52ZXN0bWVudHxlbnwwfHx8fDE3NzUyMjgzODR8MA&ixlib=rb-4.1.0&q=85"
                alt="Global Finance"
                className="rounded-sm w-full max-w-md h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Transfer Methods Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
              {t('transfer.title')}
            </h2>
            <p className="text-zinc-500">
              {t('transfer.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <Card className="card-presage rounded-sm">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#00b9ff]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#00b9ff]">W</span>
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">{t('transfer.wise.title')}</h3>
                <p className="text-zinc-500 text-sm">{t('transfer.wise.desc')}</p>
              </CardContent>
            </Card>
            <Card className="card-presage rounded-sm">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#D4AF37]">P</span>
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">{t('transfer.prc.title')}</h3>
                <p className="text-zinc-500 text-sm">{t('transfer.prc.desc')}</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-zinc-600 text-sm mt-8">
            {t('transfer.note')}
          </p>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl text-white text-center mb-12">
            {t('security.title')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, label: 'kyc' },
              { icon: Lock, label: 'encryption' },
              { icon: BarChart3, label: 'audit' },
              { icon: Eye, label: 'ai' }
            ].map((item) => (
              <Card key={item.label} className="card-presage rounded-sm">
                <CardContent className="p-6 text-center">
                  <item.icon className="h-8 w-8 text-white mx-auto mb-3" />
                  <p className="text-zinc-400 text-sm">{t(`security.${item.label}`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl text-white text-center mb-12">
            {t('faq.title')}
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {['q1', 'q2', 'q3', 'q4'].map((q, index) => (
              <AccordionItem key={q} value={q} className="border border-white/10 rounded-sm px-6 bg-[#141414]">
                <AccordionTrigger className="text-white hover:text-zinc-300 text-left" data-testid={`faq-${q}`}>
                  {t(`faq.${q}`)}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400">
                  {t(`faq.a${index + 1}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-b from-[#141414] to-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              {t('cta.subtitle')}
            </p>
            <Button 
              size="lg" 
              className="bg-white text-black hover:bg-zinc-200 rounded-sm px-12 py-6 text-lg"
              onClick={() => navigate('/register')}
              data-testid="final-cta"
            >
              {t('cta.button')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
